import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, collection, getDocs, query, orderBy, where, limit, collectionGroup } from 'firebase/firestore';
import { db } from '../firebase-config';
import {
  Container, Paper, Typography, Box, Button, CircularProgress,
  List, ListItem, ListItemText, Divider, TextField, IconButton,
  Switch, FormControlLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import { sendMessage } from '../utils/aiChat';

const ConversationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [businessData, setBusinessData] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [messageSources, setMessageSources] = useState({
    conversationDoc: 0,
    businessSubcollection: 0,
    topLevelSubcollection: 0,
    topLevelCollection: 0,
    genericSmsLogs: 0,
    smsProxyMessages: 0
  });

  // Define fetchConversation function outside useEffect so we can call it from the refresh button
  const fetchConversation = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      console.log(`Fetching conversation ${id} for user ${currentUser.uid}`);

      // First get business data
      const businessRef = doc(db, 'businesses', currentUser.uid);
      const businessSnap = await getDoc(businessRef);

      if (!businessSnap.exists()) {
        throw new Error('Business data not found');
      }

      const businessData = businessSnap.data();
      setBusinessData(businessData);
      console.log('Business data loaded:', businessData.businessInfo?.name);

      // Try to get conversation from subcollection first
      const conversationRef = doc(db, `businesses/${currentUser.uid}/conversations`, id);
      let conversationSnap = await getDoc(conversationRef);
      let conversationData = null;
      let conversationSource = '';

      // If not found in subcollection, try top-level collection
      if (!conversationSnap.exists()) {
        console.log('Conversation not found in subcollection, trying top-level collection');
        const topLevelRef = doc(db, 'conversations', id);
        const topLevelSnap = await getDoc(topLevelRef);

        if (!topLevelSnap.exists()) {
          throw new Error('Conversation not found');
        }

        conversationData = topLevelSnap.data();
        conversationSource = 'top-level';
        console.log('Found conversation in top-level collection');
      } else {
        conversationData = conversationSnap.data();
        conversationSource = 'subcollection';
        console.log('Found conversation in subcollection');
      }

      // Set conversation data
      setConversation(conversationData);

      // Extract messages from the conversation document if they exist
      // First check for messages array
      if (conversationData && conversationData.messages && Array.isArray(conversationData.messages)) {
        console.log(`Found ${conversationData.messages.length} messages in messages array`);

        // Update message sources count
        setMessageSources(prev => ({...prev, conversationDoc: conversationData.messages.length}));

        // Map messages to a consistent format
        const formattedMessages = conversationData.messages.map((msg, index) => {
          console.log(`Processing message ${index} from conversation document:`, msg);

          // Handle different message formats - IMPROVED LOGIC
          let role = 'user'; // Default to user for unknown formats

          // Check for explicit role field first
          if (msg.role) {
            role = msg.role;
          }
          // Check for sender field
          else if (msg.sender) {
            if (msg.sender === 'ai' || msg.sender === 'assistant' || msg.sender === 'business') {
              role = 'assistant';
            } else if (msg.sender === 'user' || msg.sender === 'customer') {
              role = 'user';
            }
          }
          // Check for from field
          else if (msg.from) {
            if (msg.from === 'ai' || msg.from === 'assistant' || msg.from === 'business') {
              role = 'assistant';
            } else if (msg.from === 'user' || msg.from === 'customer') {
              role = 'user';
            }
          }

          // Additional checks for customer messages

          // Check if content contains customer's number
          if (msg.content && conversationData && conversationData.customerNumber) {
            if (msg.content.includes(conversationData.customerNumber)) {
              role = 'user';
            }
          }

          // Check if this is a message from the customer's phone number
          if (conversationData && conversationData.customerNumber) {
            if (msg.from === conversationData.customerNumber) {
              role = 'user';
            }
          }

          // Check for message direction
          if (msg.direction === 'inbound') {
            role = 'user';
          }

          // Check for message body field (used in top-level messages collection)
          if (msg.body && !msg.content) {
            msg.content = msg.body;
          }

          const content = msg.content || msg.text || msg.message || '';
          const timestamp = msg.timestamp instanceof Date
            ? msg.timestamp
            : new Date(msg.timestamp?.seconds * 1000 || msg.timestamp || Date.now());

          const formattedMsg = {
            id: `msg-${index}`,
            role,
            content,
            timestamp,
            source: 'conversation_document'
          };

          console.log(`Formatted message ${index}:`, formattedMsg);
          return formattedMsg;
        });

        console.log('Formatted messages:', formattedMessages);
        setMessages(formattedMessages);
      } else if (conversationData && conversationData.messagesData && Array.isArray(conversationData.messagesData)) {
        // Try messagesData field if it exists
        console.log(`Found ${conversationData.messagesData.length} messages in messagesData field`);

        // Update message sources count
        setMessageSources(prev => ({...prev, conversationDoc: conversationData.messagesData.length}));

        // Map messagesData to a consistent format
        const formattedMessages = conversationData.messagesData.map((msg, index) => {
          console.log(`Processing message ${index} from messagesData:`, msg);

          // Handle different message formats - IMPROVED LOGIC
          let role = 'user'; // Default to user for unknown formats

          // Check for explicit role field first
          if (msg.role) {
            role = msg.role;
          }
          // Check for sender field
          else if (msg.sender) {
            if (msg.sender === 'ai' || msg.sender === 'assistant' || msg.sender === 'business') {
              role = 'assistant';
            } else if (msg.sender === 'user' || msg.sender === 'customer') {
              role = 'user';
            }
          }
          // Check for from field
          else if (msg.from) {
            if (msg.from === 'ai' || msg.from === 'assistant' || msg.from === 'business') {
              role = 'assistant';
            } else if (msg.from === 'user' || msg.from === 'customer') {
              role = 'user';
            }
          }

          // Additional check for customer messages - if content contains customer's message
          if (msg.content && conversationData && conversationData.customerNumber) {
            if (msg.content.includes(conversationData.customerNumber)) {
              role = 'user';
            }
          }

          const content = msg.content || msg.text || msg.message || '';
          const timestamp = msg.timestamp instanceof Date
            ? msg.timestamp
            : new Date(msg.timestamp?.seconds * 1000 || msg.timestamp || Date.now());

          const formattedMsg = {
            id: `msg-${index}`,
            role,
            content,
            timestamp,
            source: 'messages_data_field'
          };

          console.log(`Formatted message ${index} from messagesData:`, formattedMsg);
          return formattedMsg;
        });

        console.log('Formatted messages from messagesData:', formattedMessages);
        setMessages(formattedMessages);
      } else {
        console.log('No messages or messagesData array in conversation document, checking subcollections');

        // Try both possible subcollection paths
        try {
          // First try business subcollection
          const messagesRef = collection(db, `businesses/${currentUser.uid}/conversations/${id}/messages`);
          const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'));
          const messagesSnap = await getDocs(messagesQuery);

          if (!messagesSnap.empty) {
            console.log(`Found ${messagesSnap.docs.length} messages in business subcollection`);

            // Update message sources count
            setMessageSources(prev => ({...prev, businessSubcollection: messagesSnap.docs.length}));

            const messagesList = messagesSnap.docs.map(doc => {
              const data = doc.data();
              console.log('Message data from subcollection:', data);

              // Normalize message format - IMPROVED LOGIC
              // Handle different message formats with better detection of customer messages
              let role = 'user'; // Default to user for unknown formats

              // Check for explicit role field first
              if (data.role) {
                role = data.role;
              }
              // Check for sender field
              else if (data.sender) {
                if (data.sender === 'ai' || data.sender === 'assistant' || data.sender === 'business') {
                  role = 'assistant';
                } else if (data.sender === 'user' || data.sender === 'customer') {
                  role = 'user';
                }
              }
              // Check for from field
              else if (data.from) {
                if (data.from === 'ai' || data.from === 'assistant' || data.from === 'business') {
                  role = 'assistant';
                } else if (data.from === 'user' || data.from === 'customer') {
                  role = 'user';
                }
              }

              // Additional checks for customer messages

              // Check if content contains customer's number
              if (data.content && conversation && conversation.customerNumber) {
                if (data.content.includes(conversation.customerNumber)) {
                  role = 'user';
                }
              }

              // Check if this is a message from the customer's phone number
              if (conversation && conversation.customerNumber) {
                if (data.from === conversation.customerNumber) {
                  role = 'user';
                }
              }

              // Check for message direction
              if (data.direction === 'inbound') {
                role = 'user';
              }

              // Check for message body field (used in top-level messages collection)
              if (data.body && !data.content) {
                data.content = data.body;
              }

              const normalizedMessage = {
                id: doc.id,
                role: role,
                content: data.content || data.text || data.message || '',
                timestamp: data.timestamp instanceof Date
                  ? data.timestamp
                  : new Date(data.timestamp?.seconds * 1000 || data.timestamp || Date.now()),
                source: 'business_subcollection'
              };

              console.log('Normalized message:', normalizedMessage);
              return normalizedMessage;
            });

            console.log('Setting messages state with:', messagesList);
            setMessages(messagesList);
          } else {
            console.log('No messages in business subcollection, trying top-level subcollection');

            // Try top-level subcollection
            const topLevelMessagesRef = collection(db, `conversations/${id}/messages`);
            const topLevelMessagesQuery = query(topLevelMessagesRef, orderBy('timestamp', 'asc'));
            const topLevelMessagesSnap = await getDocs(topLevelMessagesQuery);

            if (!topLevelMessagesSnap.empty) {
              console.log(`Found ${topLevelMessagesSnap.docs.length} messages in top-level subcollection`);

              // Update message sources count
              setMessageSources(prev => ({...prev, topLevelSubcollection: topLevelMessagesSnap.docs.length}));

              const topLevelMessagesList = topLevelMessagesSnap.docs.map(doc => {
                const data = doc.data();
                // Normalize message format - IMPROVED LOGIC
                // Handle different message formats with better detection of customer messages
                let role = 'user'; // Default to user for unknown formats

                // Check for explicit role field first
                if (data.role) {
                  role = data.role;
                }
                // Check for sender field
                else if (data.sender) {
                  if (data.sender === 'ai' || data.sender === 'assistant' || data.sender === 'business') {
                    role = 'assistant';
                  } else if (data.sender === 'user' || data.sender === 'customer') {
                    role = 'user';
                  }
                }
                // Check for from field
                else if (data.from) {
                  if (data.from === 'ai' || data.from === 'assistant' || data.from === 'business') {
                    role = 'assistant';
                  } else if (data.from === 'user' || data.from === 'customer') {
                    role = 'user';
                  }
                }

                // Additional checks for customer messages

                // Check if content contains customer's number
                if (data.content && conversation && conversation.customerNumber) {
                  if (data.content.includes(conversation.customerNumber)) {
                    role = 'user';
                  }
                }

                // Check if this is a message from the customer's phone number
                if (conversation && conversation.customerNumber) {
                  if (data.from === conversation.customerNumber) {
                    role = 'user';
                  }
                }

                // Check for message direction
                if (data.direction === 'inbound') {
                  role = 'user';
                }

                // Check for message body field (used in top-level messages collection)
                if (data.body && !data.content) {
                  data.content = data.body;
                }

                return {
                  id: doc.id,
                  role: role,
                  content: data.content || data.text || data.message || '',
                  timestamp: data.timestamp instanceof Date
                    ? data.timestamp
                    : new Date(data.timestamp?.seconds * 1000 || data.timestamp || Date.now()),
                  source: 'top_level_subcollection'
                };
              });

              setMessages(topLevelMessagesList);
            } else {
              console.log('No messages found in any subcollection, using initialMessage as fallback');

              // If no messages found, use initialMessage as fallback
              if (conversationData && conversationData.initialMessage) {
                setMessages([
                  {
                    id: 'initial',
                    role: 'assistant',
                    content: conversationData.initialMessage,
                    timestamp: conversationData.createdAt instanceof Date
                      ? conversationData.createdAt
                      : new Date(conversationData.createdAt?.seconds * 1000 || conversationData.createdAt || Date.now()),
                    source: 'initial_message_fallback'
                  }
                ]);

                // Update message sources count
                setMessageSources(prev => ({...prev, conversationDoc: 1}));
              }
            }
          }
        } catch (error) {
          console.error('Error fetching messages from subcollections:', error);

          // If we can't fetch messages, use initialMessage as fallback
          if (conversationData && conversationData.initialMessage) {
            console.log('Using initialMessage as fallback due to error');
            setMessages([
              {
                id: 'initial',
                role: 'assistant',
                content: conversationData.initialMessage,
                timestamp: conversationData.createdAt instanceof Date
                  ? conversationData.createdAt
                  : new Date(conversationData.createdAt?.seconds * 1000 || conversationData.createdAt || Date.now()),
                source: 'initial_message_error_fallback'
              }
            ]);

            // Update message sources count
            setMessageSources(prev => ({...prev, conversationDoc: 1}));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to check forwarded calls for this customer
  const checkForwardedCalls = async () => {
    if (!currentUser || !conversation || !conversation.customerNumber) return null;

    try {
      console.log(`Checking forwarded_calls collection for customer ${conversation.customerNumber}`);

      // Query the forwarded_calls collection for this customer
      const callsRef = collection(db, 'forwarded_calls');
      const callsQuery = query(
        callsRef,
        where('customerNumber', '==', conversation.customerNumber),
        orderBy('timestamp', 'desc'),
        limit(1)
      );

      const callsSnapshot = await getDocs(callsQuery);

      if (callsSnapshot.empty) {
        console.log('No forwarded calls found for this customer');
        return null;
      }

      const callData = callsSnapshot.docs[0].data();
      console.log('Found forwarded call data:', callData);

      return callData;
    } catch (error) {
      console.error('Error checking forwarded calls:', error);
      return null;
    }
  };

  // Function to fetch messages from the top-level messages collection
  const fetchMessagesFromTopLevelCollection = async () => {
    if (!currentUser || !conversation || !conversation.customerNumber) return [];

    try {
      console.log(`Checking top-level messages collection for customer ${conversation.customerNumber}`);
      const allMessages = [];
      let sourceCount = 0;

      // 1. Query the top-level messages collection for messages between the business and customer
      const messagesRef = collection(db, 'messages');

      // We need to run multiple queries to catch all possible message formats
      // Query 1: messages FROM the customer (by from field)
      const fromCustomerQuery = query(
        messagesRef,
        where('from', '==', conversation.customerNumber),
        orderBy('timestamp', 'asc')
      );

      // Query 2: messages TO the customer (by to field)
      const toCustomerQuery = query(
        messagesRef,
        where('to', '==', conversation.customerNumber),
        orderBy('timestamp', 'asc')
      );

      // Execute both queries
      const fromCustomerSnapshot = await getDocs(fromCustomerQuery);
      const toCustomerSnapshot = await getDocs(toCustomerQuery);

      // Process messages FROM the customer (these are user messages)
      fromCustomerSnapshot.forEach(doc => {
        const data = doc.data();
        allMessages.push({
          id: doc.id,
          role: 'user',
          content: data.body || '',
          timestamp: data.timestamp instanceof Date
            ? data.timestamp
            : new Date(data.timestamp?.seconds * 1000 || data.timestamp || Date.now()),
          source: 'messages-from'
        });
      });

      // Process messages TO the customer (these are assistant messages)
      toCustomerSnapshot.forEach(doc => {
        const data = doc.data();
        allMessages.push({
          id: doc.id,
          role: 'assistant',
          content: data.body || '',
          timestamp: data.timestamp instanceof Date
            ? data.timestamp
            : new Date(data.timestamp?.seconds * 1000 || data.timestamp || Date.now()),
          source: 'messages-to'
        });
      });

      sourceCount = fromCustomerSnapshot.size + toCustomerSnapshot.size;

      // 2. Check generic_sms_logs collection
      try {
        const genericLogsRef = collection(db, 'generic_sms_logs');
        const genericLogsQuery = query(
          genericLogsRef,
          orderBy('timestamp', 'desc'),
          limit(50) // Get recent logs to search through
        );

        const genericLogsSnapshot = await getDocs(genericLogsQuery);
        let genericLogsCount = 0;

        genericLogsSnapshot.forEach(doc => {
          const logData = doc.data();
          // Check if this log contains data for our customer
          if (logData.data &&
              ((logData.data.From === conversation.customerNumber) ||
               (logData.data.To === conversation.customerNumber))) {

            // This is a relevant message
            const isFromCustomer = logData.data.From === conversation.customerNumber;

            allMessages.push({
              id: doc.id,
              role: isFromCustomer ? 'user' : 'assistant',
              content: logData.data.Body || '',
              timestamp: logData.timestamp instanceof Date
                ? logData.timestamp
                : new Date(logData.timestamp?.seconds * 1000 || Date.now()),
              source: 'generic_sms_logs'
            });

            genericLogsCount++;
          }
        });

        if (genericLogsCount > 0) {
          console.log(`Found ${genericLogsCount} messages in generic_sms_logs`);
          sourceCount += genericLogsCount;
          setMessageSources(prev => ({...prev, genericSmsLogs: genericLogsCount}));
        }
      } catch (error) {
        console.error('Error fetching from generic_sms_logs:', error);
      }

      // 3. Check sms_proxy_messages collection
      try {
        const smsProxyRef = collection(db, 'sms_proxy_messages');
        const fromProxyQuery = query(
          smsProxyRef,
          where('from', '==', conversation.customerNumber),
          orderBy('timestamp', 'asc')
        );

        const toProxyQuery = query(
          smsProxyRef,
          where('to', '==', conversation.customerNumber),
          orderBy('timestamp', 'asc')
        );

        const fromProxySnapshot = await getDocs(fromProxyQuery);
        const toProxySnapshot = await getDocs(toProxyQuery);

        // Process messages FROM the customer via proxy
        fromProxySnapshot.forEach(doc => {
          const data = doc.data();
          allMessages.push({
            id: doc.id,
            role: 'user',
            content: data.body || '',
            timestamp: data.timestamp instanceof Date
              ? data.timestamp
              : new Date(data.timestamp?.seconds * 1000 || data.timestamp || Date.now()),
            source: 'sms_proxy_from'
          });
        });

        // Process messages TO the customer via proxy
        toProxySnapshot.forEach(doc => {
          const data = doc.data();
          allMessages.push({
            id: doc.id,
            role: 'assistant',
            content: data.body || '',
            timestamp: data.timestamp instanceof Date
              ? data.timestamp
              : new Date(data.timestamp?.seconds * 1000 || data.timestamp || Date.now()),
            source: 'sms_proxy_to'
          });
        });

        const proxyCount = fromProxySnapshot.size + toProxySnapshot.size;
        if (proxyCount > 0) {
          console.log(`Found ${proxyCount} messages in sms_proxy_messages`);
          sourceCount += proxyCount;
          setMessageSources(prev => ({...prev, smsProxyMessages: proxyCount}));
        }
      } catch (error) {
        console.error('Error fetching from sms_proxy_messages:', error);
      }

      // Sort all messages by timestamp
      const messagesList = allMessages.sort((a, b) =>
        a.timestamp.getTime() - b.timestamp.getTime()
      );

      if (messagesList.length === 0) {
        console.log('No messages found in any top-level collections');
        return [];
      }

      console.log(`Found ${messagesList.length} total messages across all collections`);
      console.log(`- ${fromCustomerSnapshot.size} messages from customer in messages collection`);
      console.log(`- ${toCustomerSnapshot.size} messages to customer in messages collection`);

      // Update message sources count
      setMessageSources(prev => ({...prev, topLevelCollection: sourceCount}));

      return messagesList;
    } catch (error) {
      console.error('Error fetching messages from top-level collections:', error);
      return [];
    }
  };

  // Function to fetch messages from the generic_sms_logs collection directly
  const fetchMessagesFromGenericSmsLogs = async () => {
    if (!currentUser || !conversation || !conversation.customerNumber) return [];

    try {
      console.log(`Directly checking generic_sms_logs for customer ${conversation.customerNumber}`);
      const allMessages = [];

      // Try multiple approaches to find all messages

      // Approach 1: Query by conversation ID if available
      if (conversation.id) {
        try {
          const conversationIdQuery = query(
            collection(db, 'generic_sms_logs'),
            where('conversationId', '==', conversation.id),
            orderBy('timestamp', 'desc'),
            limit(100)
          );

          const conversationIdSnapshot = await getDocs(conversationIdQuery);
          console.log(`Found ${conversationIdSnapshot.size} logs with matching conversationId`);

          conversationIdSnapshot.forEach(doc => {
            const logData = doc.data();
            if (logData.data && logData.data.Body) {
              const isFromCustomer = logData.data.From === conversation.customerNumber;

              allMessages.push({
                id: doc.id,
                role: isFromCustomer ? 'user' : 'assistant',
                content: logData.data.Body || '',
                timestamp: logData.timestamp instanceof Date
                  ? logData.timestamp
                  : new Date(logData.timestamp?.seconds * 1000 || Date.now()),
                source: 'generic_sms_logs_by_conversation_id'
              });
            }
          });
        } catch (error) {
          console.error('Error querying by conversationId:', error);
        }
      }

      // Approach 2: Query by customer number in From/To fields
      try {
        // Query for messages FROM the customer
        const fromCustomerQuery = query(
          collection(db, 'generic_sms_logs'),
          where('data.From', '==', conversation.customerNumber),
          orderBy('timestamp', 'desc'),
          limit(50)
        );

        const fromCustomerSnapshot = await getDocs(fromCustomerQuery);
        console.log(`Found ${fromCustomerSnapshot.size} logs with customer as sender`);

        // Query for messages TO the customer
        const toCustomerQuery = query(
          collection(db, 'generic_sms_logs'),
          where('data.To', '==', conversation.customerNumber),
          orderBy('timestamp', 'desc'),
          limit(50)
        );

        const toCustomerSnapshot = await getDocs(toCustomerQuery);
        console.log(`Found ${toCustomerSnapshot.size} logs with customer as recipient`);

        // Process messages FROM the customer
        fromCustomerSnapshot.forEach(doc => {
          const logData = doc.data();
          if (logData.data && logData.data.Body) {
            allMessages.push({
              id: doc.id,
              role: 'user',
              content: logData.data.Body || '',
              timestamp: logData.timestamp instanceof Date
                ? logData.timestamp
                : new Date(logData.timestamp?.seconds * 1000 || Date.now()),
              source: 'generic_sms_logs_from_customer'
            });
          }
        });

        // Process messages TO the customer
        toCustomerSnapshot.forEach(doc => {
          const logData = doc.data();
          if (logData.data && logData.data.Body) {
            allMessages.push({
              id: doc.id,
              role: 'assistant',
              content: logData.data.Body || '',
              timestamp: logData.timestamp instanceof Date
                ? logData.timestamp
                : new Date(logData.timestamp?.seconds * 1000 || Date.now()),
              source: 'generic_sms_logs_to_customer'
            });
          }
        });
      } catch (error) {
        console.error('Error querying by From/To fields:', error);
      }

      // Approach 3: Fallback to scanning recent logs
      if (allMessages.length === 0) {
        try {
          const genericLogsRef = collection(db, 'generic_sms_logs');
          const genericLogsQuery = query(
            genericLogsRef,
            orderBy('timestamp', 'desc'),
            limit(200) // Get more logs to ensure we catch all relevant ones
          );

          const genericLogsSnapshot = await getDocs(genericLogsQuery);
          console.log(`Scanning ${genericLogsSnapshot.size} recent logs for customer ${conversation.customerNumber}`);

          genericLogsSnapshot.forEach(doc => {
            const logData = doc.data();
            // Check if this log contains data for our customer
            if (logData.data &&
                ((logData.data.From === conversation.customerNumber) ||
                 (logData.data.To === conversation.customerNumber))) {

              // This is a relevant message
              const isFromCustomer = logData.data.From === conversation.customerNumber;

              allMessages.push({
                id: doc.id,
                role: isFromCustomer ? 'user' : 'assistant',
                content: logData.data.Body || '',
                timestamp: logData.timestamp instanceof Date
                  ? logData.timestamp
                  : new Date(logData.timestamp?.seconds * 1000 || Date.now()),
                source: 'generic_sms_logs_scan'
              });
            }
          });
        } catch (error) {
          console.error('Error scanning recent logs:', error);
        }
      }

      // Remove duplicates by ID
      const uniqueMessages = [];
      const seenIds = new Set();

      allMessages.forEach(msg => {
        if (!seenIds.has(msg.id)) {
          seenIds.add(msg.id);
          uniqueMessages.push(msg);
        }
      });

      if (uniqueMessages.length > 0) {
        console.log(`Found ${uniqueMessages.length} unique messages directly in generic_sms_logs`);
        setMessageSources(prev => ({...prev, genericSmsLogs: uniqueMessages.length}));

        // Sort by timestamp
        return uniqueMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      }

      return [];
    } catch (error) {
      console.error('Error directly fetching from generic_sms_logs:', error);
      return [];
    }
  };

  // Function to fetch the most recent messages from any collection
  const fetchMostRecentMessages = async () => {
    if (!currentUser || !conversation || !conversation.customerNumber) return [];

    try {
      console.log(`Fetching most recent messages for customer ${conversation.customerNumber}`);
      const allMessages = [];

      // 1. Check generic_sms_logs for the most recent messages
      const genericLogsRef = collection(db, 'generic_sms_logs');
      const genericLogsQuery = query(
        genericLogsRef,
        orderBy('timestamp', 'desc'),
        limit(20) // Get the most recent logs
      );

      const genericLogsSnapshot = await getDocs(genericLogsQuery);
      let recentMessagesCount = 0;

      // Process the logs to find the most recent messages for this customer
      genericLogsSnapshot.forEach(doc => {
        const logData = doc.data();
        if (logData.data &&
            ((logData.data.From === conversation.customerNumber) ||
             (logData.data.To === conversation.customerNumber))) {

          const isFromCustomer = logData.data.From === conversation.customerNumber;

          allMessages.push({
            id: doc.id,
            role: isFromCustomer ? 'user' : 'assistant',
            content: logData.data.Body || '',
            timestamp: logData.timestamp instanceof Date
              ? logData.timestamp
              : new Date(logData.timestamp?.seconds * 1000 || Date.now()),
            source: 'recent_generic_sms_logs'
          });

          recentMessagesCount++;
        }
      });

      if (recentMessagesCount > 0) {
        console.log(`Found ${recentMessagesCount} recent messages in generic_sms_logs`);
      }

      // Sort by timestamp (oldest first)
      return allMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    } catch (error) {
      console.error('Error fetching most recent messages:', error);
      return [];
    }
  };

  // Fetch conversation and messages on component mount
  useEffect(() => {
    const loadAllMessages = async () => {
      await fetchConversation();

      // Add a small delay to ensure conversation state is updated
      setTimeout(async () => {
        if (!conversation || !conversation.customerNumber) {
          console.log('No conversation or customer number available, skipping additional message fetching');
          return;
        }

      // Try multiple sources to get all messages
      console.log('Fetching messages from all possible sources');

      // 1. Check top-level messages collection
      const topLevelMessages = await fetchMessagesFromTopLevelCollection();

      // 2. Check generic_sms_logs directly as a fallback
      const genericSmsMessages = await fetchMessagesFromGenericSmsLogs();

      // 3. Get the most recent messages from any collection
      const recentMessages = await fetchMostRecentMessages();

      // 4. Check forwarded calls for additional context
      const forwardedCallData = await checkForwardedCalls();

      // Combine all messages
      let allMessages = [...messages];
      let additionalMessagesCount = 0;

      // Add messages from top-level collection
      if (topLevelMessages.length > 0) {
        allMessages = [...allMessages, ...topLevelMessages];
        additionalMessagesCount += topLevelMessages.length;
        console.log(`Added ${topLevelMessages.length} messages from top-level collection`);
      }

      // Add messages from generic_sms_logs
      if (genericSmsMessages.length > 0) {
        allMessages = [...allMessages, ...genericSmsMessages];
        additionalMessagesCount += genericSmsMessages.length;
        console.log(`Added ${genericSmsMessages.length} messages from generic_sms_logs`);
      }

      // Add recent messages
      if (recentMessages.length > 0) {
        allMessages = [...allMessages, ...recentMessages];
        additionalMessagesCount += recentMessages.length;
        console.log(`Added ${recentMessages.length} recent messages`);
      }

      // If we found a forwarded call with a message, add it
      if (forwardedCallData && forwardedCallData.webhookData && forwardedCallData.webhookData.Body) {
        const callMessage = {
          id: `call-${forwardedCallData.callSid || Date.now()}`,
          role: 'user',
          content: forwardedCallData.webhookData.Body,
          timestamp: forwardedCallData.timestamp instanceof Date
            ? forwardedCallData.timestamp
            : new Date(forwardedCallData.timestamp?.seconds * 1000 || Date.now()),
          source: 'forwarded_call'
        };

        allMessages.push(callMessage);
        additionalMessagesCount++;
        console.log('Added message from forwarded call data');
      }

      if (additionalMessagesCount > 0) {
        // Sort all messages by timestamp
        allMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        // Improved deduplication logic
        const uniqueMessages = [];
        const seen = new Set();

        allMessages.forEach(msg => {
          // Create a key that includes content and approximate timestamp (rounded to nearest minute)
          // This helps catch duplicates even if timestamps are slightly different
          const timestampMinutes = Math.floor(msg.timestamp.getTime() / (60 * 1000));
          const key = `${msg.role}-${msg.content}-${timestampMinutes}`;

          if (!seen.has(key)) {
            seen.add(key);
            uniqueMessages.push(msg);
          } else {
            console.log(`Skipping duplicate message: ${msg.content.substring(0, 30)}...`);
          }
        });

        console.log(`Combined ${messages.length} original messages with ${additionalMessagesCount} additional messages for a total of ${uniqueMessages.length} unique messages`);
        setMessages(uniqueMessages);
      } else {
        // If we still don't have any messages, try one more approach - look at the most recent logs
        if (messages.length <= 1) {
          console.log('Only found initial message, trying to fetch most recent logs as a last resort');

          // Make a direct query to the generic_sms_logs collection for the most recent entries
          try {
            const logsRef = collection(db, 'generic_sms_logs');
            const recentLogsQuery = query(
              logsRef,
              orderBy('timestamp', 'desc'),
              limit(10)
            );

            const logsSnapshot = await getDocs(recentLogsQuery);
            console.log(`Found ${logsSnapshot.size} recent logs to check`);

            // Log the most recent entries for debugging
            logsSnapshot.forEach(doc => {
              const data = doc.data();
              console.log('Recent log:', data);
            });
          } catch (error) {
            console.error('Error fetching recent logs:', error);
          }

          console.log('No additional messages found, keeping existing messages');
        } else {
          console.log('No additional messages found, keeping existing messages');
        }
      }
      }, 100); // 100ms delay to ensure state is updated
    };

    loadAllMessages();
  }, [currentUser, id]);

  // Handle sending a new message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversation || !businessData) return;

    setSending(true);
    try {
      // Add message to UI immediately
      const userMessage = {
        id: `temp-${Date.now()}`,
        content: newMessage,
        role: 'user',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, userMessage]);
      setNewMessage('');

      // Send message to AI
      const response = await sendMessage(id, newMessage, currentUser.uid, businessData);

      if (response && response.data && response.data.reply) {
        // Add AI response to UI
        const aiMessage = {
          id: response.data.messageId || `ai-${Date.now()}`,
          content: response.data.reply,
          role: 'assistant',
          timestamp: new Date()
        };

        setMessages(prev => [...prev, aiMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading conversation...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 3, bgcolor: 'error.light' }}>
          <Typography variant="h6">Error</Typography>
          <Typography variant="body1">{error}</Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  // Debug log current state
  console.log('Rendering conversation view with messages:', messages);

  // Log message roles for debugging
  const assistantCount = messages.filter(m => m.role === 'assistant').length;
  const userCount = messages.filter(m => m.role === 'user').length;
  console.log(`Message breakdown: ${assistantCount} assistant messages, ${userCount} user messages`);

  // Log customer number for reference
  if (conversation && conversation.customerNumber) {
    console.log(`Customer number: ${conversation.customerNumber}`);
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Conversation with {conversation?.customerNumber || 'Customer'}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setLoading(true);
              fetchConversation();
            }}
          >
            Refresh
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Debug toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={debugMode}
                onChange={(e) => setDebugMode(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="caption">Debug Mode</Typography>}
          />
        </Box>

        {/* Debug info */}
        <Box sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, display: debugMode ? 'block' : 'none' }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', fontWeight: 'bold' }}>
            Debug Information
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
            Found {messages.length} messages ({messages.filter(m => m.role === 'assistant').length} assistant, {messages.filter(m => m.role === 'user').length} user)
          </Typography>
          {messages.length > 0 && (
            <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
              First message: {messages[0].role} - {messages[0].content.substring(0, 30)}...
            </Typography>
          )}
          {messages.length > 1 && (
            <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
              Last message: {messages[messages.length-1].role} - {messages[messages.length-1].content.substring(0, 30)}...
            </Typography>
          )}
          {conversation && conversation.customerNumber && (
            <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
              Customer: {conversation.customerNumber}
            </Typography>
          )}

          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', fontWeight: 'bold', mt: 1 }}>
            Message Sources:
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
            Conversation Document: {messageSources.conversationDoc}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
            Business Subcollection: {messageSources.businessSubcollection}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
            Top-Level Subcollection: {messageSources.topLevelSubcollection}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
            Top-Level Collection: {messageSources.topLevelCollection}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
            Generic SMS Logs: {messageSources.genericSmsLogs}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
            SMS Proxy Messages: {messageSources.smsProxyMessages}
          </Typography>
        </Box>

        <Paper elevation={1} sx={{ p: 2, mb: 3, maxHeight: '60vh', overflow: 'auto' }}>
          <List>
            {messages && messages.length > 0 ? messages.map((message, index) => {
              console.log(`Rendering message ${index}:`, message);
              return (
                <React.Fragment key={message.id || index}>
                  <ListItem alignItems="flex-start" sx={{
                    bgcolor: message.role === 'assistant' ? 'primary.light' : 'background.paper',
                    borderRadius: 2,
                    mb: 1
                  }}>
                    <ListItemText
                      primary={message.role === 'assistant' ? 'Business' : 'Customer'}
                      secondary={
                        <>
                          <Typography component="span" variant="body1" color="text.primary">
                            {message.content || "(No content)"}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            {message.timestamp instanceof Date
                              ? message.timestamp.toLocaleString()
                              : new Date(message.timestamp?.seconds * 1000 || Date.now()).toLocaleString()}
                            {debugMode && message.source && (
                              <span style={{ marginLeft: '8px', color: '#666', fontSize: '0.8em' }}>
                                [{message.source}]
                              </span>
                            )}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < messages.length - 1 && <Box sx={{ height: 8 }} />}
                </React.Fragment>
              );
            }) : (
              <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                No messages in this conversation.
              </Typography>
            )}
          </List>
        </Paper>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
            disabled={sending}
          />
          <Button
            variant="contained"
            color="primary"
            endIcon={<SendIcon />}
            onClick={handleSendMessage}
            disabled={sending || !newMessage.trim()}
          >
            {sending ? <CircularProgress size={24} /> : 'Send'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ConversationView;
